// Who drives whom, from links, formulas and definitions: structure for navigation and the graph, not maths.

import { linkFrom, type ModelNode, type Link } from "./types";

/**
 * Direct drivers of a node (handles of the nodes it depends on): the links into it, plus the nodes its
 * formula or definition uses. Those come from the API (`GET /worldview` lists each node's derived drivers),
 * so the app never looks inside a formula.
 */
export function drivers(nodeId: string, nodes: Record<string, ModelNode>, links: Link[]): string[] {
	const out = new Set<string>();
	const n = nodes[nodeId];
	if (!n) return [];
	for (const l of links) if (l.to === nodeId) out.add(linkFrom(l));
	for (const h of n.inputs) out.add(h);
	out.delete(nodeId);
	return [...out].filter((id) => nodes[id]);
}

export function ancestors(nodeId: string, nodes: Record<string, ModelNode>, links: Link[]): Set<string> {
	const seen = new Set<string>();
	const stack = [nodeId];
	while (stack.length) {
		const id = stack.pop()!;
		for (const d of drivers(id, nodes, links)) {
			if (!seen.has(d)) {
				seen.add(d);
				stack.push(d);
			}
		}
	}
	seen.delete(nodeId);
	return seen;
}

export function dependants(nodeId: string, nodes: Record<string, ModelNode>, links: Link[]): string[] {
	return Object.keys(nodes).filter((id) => drivers(id, nodes, links).includes(nodeId));
}

/** Everything downstream of a node. */
export function downstream(id: string, nodes: Record<string, ModelNode>, links: Link[]): Set<string> {
	const seen = new Set<string>();
	const stack = [id];
	while (stack.length) {
		for (const d of dependants(stack.pop()!, nodes, links)) {
			if (!seen.has(d)) {
				seen.add(d);
				stack.push(d);
			}
		}
	}
	return seen;
}

/** Calculated nodes follow their inputs, so a scenario can't set them directly. */
export function isCalculated(n: ModelNode): boolean {
	return (n.kind === "event" && !!n.definition) || (n.kind === "metric" && !!n.formula);
}
